// 1. Import `extendTheme`
import { extendTheme, theme as t } from "@chakra-ui/react";
// 2. Call `extendTheme` and pass your custom values

export const ployTheme = extendTheme(
  {
    styles: {
      global: {
        body: {
          bg: "rgb(250, 250, 250)",
          // Crisper text on macOS — without this Hubot/Archivo render
          // heavier than designed. Harmless on Windows/Linux.
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },
  },
  {
    fonts: {
      heading: `'HubotSans', sans-serif`,
      body: `'Archivo', sans-serif`,
    },
    fontWeights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    fontSizes: {
      // Matches Figma typography scale tokens
      "3xs": "8px", // scale 0
      "2xs": "10px", // scale 1
      xs: "12px", // scale 2
      smd: "13px", // custom (between scale 2 and 3) — dominant secondary size in redesign
      md: "14px", // scale 3
      mdl: "15px", // custom (between scale 3 and 4) — recip-row, wizard panel titles
      lg: "16px", // scale 4
      xl: "20px", // scale 5
      xl1: "22px", // custom (between 5 and 6) — freq-expanded display title
      "2xl": "24px", // scale 6
      "2xl1": "26px", // custom (between 6 and 7) — ra-modal title
      "2xl2": "28px", // custom — review summary stat numbers, wizard v2 title
      "3xl": "32px", // scale 7
      "4xl": "40px", // scale 8
      "5xl": "48px", // scale 9
      "6xl": "64px", // scale 10
      "7xl": "128px", // scale 11 — hero cover only
    },
    space: {
      // Matches Figma spacing tokens (Light Mode)
      "2xs": "4px", // spacing-2xs
      "1xs": "6px", // spacing-1xs
      sm: "8px", // spacing-sm
      md: "12px", // spacing-md
      lg: "16px", // spacing-lg
      "1xl": "24px", // spacing-1xl
      "2xl": "32px", // spacing-2xl
      "3xl": "40px", // spacing-3xl
      "4xl": "48px", // spacing-4xl (primitive spacing/9)
      "5xl": "56px", // spacing-5xl (primitive spacing/10)
      "6xl": "64px", // spacing-6xl (primitive spacing/11)
      "7xl": "72px", // spacing-7xl (primitive spacing/12)
      "8xl": "80px", // spacing-8xl (primitive spacing/13)
      // Backwards compatibility aliases (deprecated - use Figma names above)
      xs: "8px", // @deprecated use "sm"
      xl: "24px", // @deprecated use "1xl"
    },
    radii: {
      // Matches Figma radius tokens (Light Mode)
      component: {
        sm: "4px", // radius-small-component
        md: "6px", // radius-small-component-wrapper
        md1: "7px", // applogo, config-row__trash, ra-drawer table corners
        lg: "8px", // radius-medium-component
        xl: "10px", // radius-medium-component-wrapper
        "2xl": "12px", // radius-large-component
        "3xl": "14px", // radius-large-component-wrapper
        "4xl": "16px", // radius-xl-component
      },
      container: {
        sm: "18px", // radius-small-container
        md: "24px", // radius-small-container-wrapper
        lg: "32px", // radius-large-container
        xl: "40px", // radius-large-container-wrapper
        // Backwards compatibility alias
        "2xl": "40px", // @deprecated use "xl"
      },
    },
    shadows: {
      ...t.shadows,
      sm: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      md: "0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)",
      xs: "0px 1px 2px rgba(16, 24, 40, 0.05)",
      "solid-btn":
        "0 1px 3px rgba(30, 90, 180, 0.35), 0 2px 6px rgba(30, 90, 180, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.18)",
      "outline-btn":
        "0 0.5px 1px 0 rgba(0, 0, 0, 0.25) inset, 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 0.5px 0 rgba(0, 0, 0, 0.25)",
      "outline-btn-primary":
        "0 0.5px 1px 0 rgba(54, 59, 143, 0.30) inset, 0 1px 1px 0 rgba(54, 59, 143, 0.12), 0 0 0.5px 0 rgba(54, 59, 143, 0.30)",
      "outline-btn-success":
        "0 0.5px 1px 0 rgba(6, 95, 70, 0.30) inset, 0 1px 1px 0 rgba(6, 95, 70, 0.12), 0 0 0.5px 0 rgba(6, 95, 70, 0.30)",
      "outline-btn-error":
        "0 0.5px 1px 0 rgba(155, 44, 44, 0.30) inset, 0 1px 1px 0 rgba(155, 44, 44, 0.12), 0 0 0.5px 0 rgba(155, 44, 44, 0.30)",
      "outline-btn-warning":
        "0 0.5px 1px 0 rgba(146, 64, 14, 0.30) inset, 0 1px 1px 0 rgba(146, 64, 14, 0.12), 0 0 0.5px 0 rgba(146, 64, 14, 0.30)",
      "outline-btn-luna":
        "0 0.5px 1px 0 rgba(58, 63, 156, 0.30) inset, 0 1px 1px 0 rgba(58, 63, 156, 0.12), 0 0 0.5px 0 rgba(58, 63, 156, 0.30)",

      // Segmented-control selected thumb. Mirrors the main app's `seg-thumb`
      // (ploy-client theme) so the ported SegmentedControl matches it exactly.
      "seg-thumb": "0px 0px 2px 0px rgba(0,0,0,0.24)",

      // Avatar ring — neutral.0 at 30%, drawn INSIDE the disc as an inset ring
      // rather than a border, so the ring never adds a hard white outline
      // around the avatar's silhouette.
      "avatar-ring": "inset 0 0 0 2px rgba(254, 254, 254, 0.30)",

      // Design system: etched-edge button elevation. Stacks inset highlight +
      // outer halo + base elevation. Used on cards, dropdowns, dropshadow ub.
      elevLow:
        "inset 0 1px 0 rgba(255,255,255,1), 0 1px 0 rgba(0,0,0,0.04), 0 0 1px 0 rgba(0,0,0,0.32), 0 0 2px 0 rgba(0,0,0,0.08), 0 1px 3px 0 rgba(0,0,0,0.08)",
      // Midpoint between elevLow and elevHigh: the shared etched-edge stack +
      // a half-strength drop (blur 3→7.2 ≈ 5, alpha 0.08→0.25 ≈ 0.16). For
      // surfaces that float above content at rest (not just on hover) —
      // first call site: the request modal's condensed duration-config card.
      elevMid:
        "inset 0 1px 0 rgba(255,255,255,1), 0 1px 0 rgba(0,0,0,0.04), 0 0 1px 0 rgba(0,0,0,0.32), 0 0 2px 0 rgba(0,0,0,0.08), 0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 5px 0 rgba(0,0,0,0.16)",
      // Hover state for elevLow components.
      elevHigh:
        "inset 0 1px 0 rgba(255,255,255,1), 0 1px 0 rgba(0,0,0,0.04), 0px 1px 3px 0px rgba(0,0,0,0.08), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 0px 1px 0px rgba(0,0,0,0.32), 0px 1px 7.2px 0px rgba(0,0,0,0.25)",
      // Overlay shadow for menus, popovers, tooltips
      overlay:
        "0px 4px 6px -2px rgba(16,24,40,0.03), 0px 12px 16px -4px rgba(16,24,40,0.08)",
      // Left-facing drawer slide shadow
      drawerLeft:
        "-16px 0 32px rgba(16,24,40,0.08), -4px 0 8px rgba(16,24,40,0.04)",
      // Modal deep shadow
      modal: "0 20px 50px rgba(0,0,0,0.25)",
      // Wizard / large modal shadow
      wizard: "0 24px 60px rgba(0,0,0,0.25)",
      // Popover-style fallback shadow
      popover: "0 12px 32px rgba(16,24,40,0.12)",
      // Toast shadow
      toast: "0 12px 24px rgba(0,0,0,0.2)",
      // Tooltip drop shadow
      tooltip: "0 4px 12px rgba(0,0,0,0.18)",
      // Azurite focus ring (3px halo at 25% opacity)
      focusRing: "0 0 0 3px rgba(113,122,247,0.25)",
      // Softer azurite focus ring (3px halo at 18%) — used on ra-cat-card
      focusRingSubtle: "0 0 0 3px rgba(113,122,247,0.18)",
      // Tinted primary-button shadow stack (azurite tint)
      buttonPrimary:
        "0 1px 3px rgba(30,90,180,0.35), 0 2px 6px rgba(30,90,180,0.2), inset 0 1px 0 rgba(255,255,255,0.18)",
      // Tinted success-button shadow stack
      buttonSuccess:
        "0 1px 3px rgba(30,130,70,0.35), 0 2px 6px rgba(30,130,70,0.2), inset 0 1px 0 rgba(255,255,255,0.18)",
      // Tinted danger-button shadow stack
      buttonDanger:
        "0 1px 3px rgba(180,35,35,0.35), 0 2px 6px rgba(180,35,35,0.2), inset 0 1px 0 rgba(255,255,255,0.18)",
      // Soft tone-ring used on avatars
      avatarRing: "0 0 0 2px",
    },
    components: {
      Card: {
        baseStyle: {
          container: {
            boxShadow: "none",
            border: "none",
          },
        },
        variants: {
          elevated: {
            container: {
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "neutral.200",
            },
          },
        },
      },
      Menu: {
        baseStyle: {
          list: {
            borderRadius: "component.2xl",
            overflow: "hidden",
          },
        },
      },
      Popover: {
        baseStyle: {
          popper: {
            zIndex: 25,
          },
        },
        variants: {
          alwaysVisible: {
            popper: {
              zIndex: 9999,
            },
          },
        },
      },
      Drawer: {
        variants: {
          alwaysOpen: {
            parts: ["dialog, dialogContainer"],
            dialog: {
              pointerEvents: "auto",
            },
            dialogContainer: {
              pointerEvents: "none",
            },
          },
        },
      },
      Checkbox: {
        baseStyle: {
          control: {
            borderRadius: "component.sm",
            borderColor: "neutral.300",
            _checked: {
              bg: "p.50",
              borderColor: "p.600",
              color: "p.600",
              _hover: {
                bg: "p.50",
                borderColor: "p.600",
              },
            },
            _hover: {
              bg: "p.50",
              borderColor: "p.600",
            },
          },
        },
      },
      Tag: {
        baseStyle: {
          container: {
            paddingInlineEnd: "var(--chakra-space-2)",
            paddingInlineStart: "var(--chakra-space-2)",
          },
        },
        variants: {
          solid: {
            container: {
              borderRadius: "container.sm",
            },
            label: {
              mixBlendMode: "multiply",
            },
          },
          subtle: {
            container: {
              borderRadius: "component.md",
              shadow: "xs",
            },
            label: {
              mixBlendMode: "multiply",
            },
          },
        },
      },
      Table: {
        variants: {
          striped: {
            th: {
              color: "neutral.600",
              fontSize: "xs",
              fontWeight: "600",
            },
            td: {
              py: 4,
            },
            tbody: {
              tr: {
                "&:nth-of-type(odd)": {
                  td: {
                    background: "neutral.50",
                  },
                },
              },
            },
          },
        },
      },
      Divider: {
        baseStyle: {
          borderColor: "neutral.200",
        },
      },
      Button: {
        variants: {
          primary: {
            bg: "p.600",
            shadow: "xs",
            color: "white",
            borderRadius: "component.lg",
            _disabled: {
              color: "p.800",
              bg: "p.200",
              _hover: {
                bg: "p.500 !important",
                color: "p.100",
              },
            },
            _hover: {
              bg: "p.700",
            },
          },
          secondary: {
            bg: "white",
            borderColor: "neutral.300",
            borderWidth: "1px",
            borderStyle: "solid",
            shadow: "xs",
            color: "neutral.950",
            borderRadius: "component.lg",
          },
        },
      },
      Tabs: {
        variants: {
          "enclosed-colored": {
            tabList: {
              borderBottom: "solid 1px",
              borderBottomColor: "neutral.200",
            },
            tab: {
              _selected: {
                fontWeight: 500,
                color: "neutral.950",
                borderTopColor: "neutral.200",
              },
              _hover: {
                fontWeight: 500,
                color: "neutral.950",
              },
              borderTopRightRadius: "5px",
              borderTopLeftRadius: "5px",
              fontWeight: 400,
              fontSize: "13px",
              color: "neutral.600",
            },
          },
          enclosed: {
            //a: t.components.Tabs.variants?.enclosed().tablist.borderBottom,
            tablist: {
              p: 1,
              bg: "neutral.50",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "neutral.100",
              borderBottomColor: "neutral.100",
              borderTopColor: "neutral.100",
              borderLeftColor: "neutral.100",
              borderRightColor: "neutral.100",
              borderBottom: "1px solid",
              borderRadius: "component.md",
              shadow: "sm",
            },
            tab: {
              fontWeight: 600,
              mr: 2,
              _selected: {
                borderRadius: "component.md",
                bg: "white",
                shadow: "sm",
                fontWeight: 600,
                color: "neutral.950",
              },
              _hover: {
                borderRadius: "component.md",
                bg: "white",
                shadow: "sm",
                fontWeight: 600,
                color: "neutral.950",
              },
              color: "neutral.700",
            },
          },
          line: {
            tab: {
              fontWeight: 600,
              _selected: {
                color: "p.700",
                borderColor: "p.700",
              },
              color: "neutral.700",
            },
          },
        },
      },
      Input: {
        variants: {
          outline: (props: any) => ({
            field: {
              bg: "white",
              borderColor: props.isReadOnly ? "transparent" : "neutral.200",
              ":hover": {
                borderColor: props.isReadOnly ? "transparent" : "neutral.200",
              },
              "--input-height": props.isReadOnly
                ? "auto"
                : t.components.Input.variants?.outline(props).field[
                    "--input-height"
                  ],
              cursor: props.isReadOnly ? "default" : "auto",
              pl: props.isReadOnly ? 0 : undefined,
              borderRadius: "component.md",
              shadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
            },
          }),
        },
      },
      Textarea: {
        variants: {
          outline: (props: any) => ({
            bg: "white",
            borderColor: props.isReadOnly ? "transparent" : "neutral.200",
            cursor: props.isReadOnly ? "default" : "auto",
            ":hover": {
              borderColor: props.isReadOnly ? "transparent" : "neutral.200",
            },
            pl: props.isReadOnly ? 0 : 4,
            borderRadius: "component.md",
            shadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
          }),
        },
      },
      Select: {
        variants: {
          outline: {
            field: {
              bg: "white",
              borderColor: "neutral.200",
              borderRadius: "component.md",
              shadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
            },
          },
        },
      },
      Text: {
        baseStyle: {
          letterSpacing: "-0.2px",
        },
        variants: {
          lil: {
            fontWeight: 400,
            color: "neutral.600",
            fontSize: "sm",
          },
          label: {
            fontWeight: 500,
            color: "neutral.950",
            fontSize: "sm",
          },
          labelSub: {
            fontWeight: 400,
            color: "neutral.600",
            fontSize: "xs",
          },
          sm6: {
            fontWeight: 600,
            color: "neutral.950",
            fontSize: "sm",
          },
          "xs.sb": {
            fontWeight: "600",
            fontSize: "xs",
            color: "neutral.600",
          },
          "xs.d.sb": {
            fontWeight: "600",
            fontSize: "2xl",
            color: "neutral.950",
          },
          "sm.d.sb": {
            fontWeight: "600",
            fontSize: "3xl",
            color: "neutral.900",
          },
          "md.m": {
            fontWeight: "500",
            fontSize: "md",
            color: "neutral.950",
          },
          "md.d.b": {
            fontWeight: "700",
            fontSize: "4xl",
            color: "neutral.950",
          },
          "md.d.sb": {
            fontWeight: "600",
            fontSize: "4xl",
            color: "neutral.900",
          },
          "md.sb": {
            fontWeight: "600",
            fontSize: "md",
            color: "neutral.950",
          },
          "sm.sb": {
            fontWeight: "600",
            fontSize: "sm",
            color: "neutral.950",
          },
          "sm.m": {
            fontWeight: "500",
            fontSize: "sm",
            color: "neutral.950",
          },
          "xs.m": {
            fontWeight: "500",
            fontSize: "xs",
            color: "neutral.950",
          },
          "lg.m": {
            fontWeight: "500",
            fontSize: "lg",
            color: "neutral.950",
          },
          "lg.sb": {
            fontSize: "lg",
            fontWeight: "600",
            color: "neutral.900",
          },
        },
      },
    },
    colors: {
      // Old palettes (kept for backwards compatibility)
      green: {
        25: "#F6FEF9",
        50: "#ECFDF3",
        100: "#D1FADF",
        200: "#A6F4C5",
        300: "#6CE9A6",
        400: "#32D583",
        500: "#12B76A",
        600: "#039855",
        700: "#027A48",
        800: "#05603A",
        900: "#054F31",
      },
      orange: {
        25: "#FFFCF5",
        50: "#FFFAEB",
        100: "#FEF0C7",
        200: "#FEDF89",
        300: "#FEC84B",
        400: "#FDB022",
        500: "#F79009",
        600: "#DC6803",
        700: "#B54708",
        800: "#93370D",
        900: "#7A2E0E",
      },
      red: {
        25: "#FFFBFA",
        50: "#FEF3F2",
        100: "#FEE4E2",
        200: "#FECDCA",
        300: "#FDA29B",
        400: "#F97066",
        500: "#F04438",
        600: "#D92D20",
        700: "#B42318",
        800: "#912018",
        900: "#7A271A",
      },
      gray: {
        25: "#FCFCFD",
        50: "#F9FAFB",
        100: "#F2F4F7",
        200: "#EAECF0",
        300: "#D0D5DD",
        400: "#98A2B3",
        500: "#667085",
        600: "#475467",
        700: "#344054",
        800: "#1D2939",
        900: "#101828",
      },

      // Semantic color palettes (Figma: Success, Warning, Error)
      success: {
        50: "#ECFDF5",
        100: "#D1FAE5",
        200: "#A7F3D0",
        300: "#6EE7B7",
        400: "#34D399",
        500: "#10B981",
        600: "#059669",
        700: "#047857",
        800: "#065F46",
        900: "#064E3B",
      },
      warning: {
        50: "#FFFBEB",
        100: "#FEF3C7",
        200: "#FDE68A",
        300: "#FCD34D",
        400: "#FBBF24",
        500: "#F59E0B",
        600: "#D97706",
        700: "#B45309",
        800: "#92400E",
        900: "#92400E",
      },
      error: {
        50: "#FFF5F5",
        100: "#FED7D7",
        200: "#FEB2B2",
        300: "#FC8181",
        400: "#F56565",
        500: "#FF524F",
        600: "#E53E3E",
        700: "#C53030",
        800: "#9B2C2C",
        900: "#742A2A",
      },

      // Primary brand color (Azurite)
      p: {
        50: "#F5F6FE",
        100: "#E8EAFD",
        200: "#D4D7FB",
        300: "#B5BBF9",
        400: "#939DF8",
        500: "#717AF7",
        600: "#5861DE",
        700: "#4349B8",
        800: "#363B8F",
        900: "#2D3291",
        grad: "#5861DE",
      },
      azurite: {
        // Alias for 'p' for clarity
        50: "#F5F6FE",
        100: "#E8EAFD",
        200: "#D4D7FB",
        300: "#B5BBF9",
        400: "#939DF8",
        500: "#717AF7",
        600: "#5861DE",
        700: "#4349B8",
        800: "#363B8F",
        900: "#2D3291",
      },

      // Secondary brand color
      p2: {
        25: "#F4EBFF",
        50: "#E9D7FE",
        100: "#D6BBFB",
        200: "#B692F6",
        300: "#9E77ED",
        400: "#7F56D9",
        500: "#6941C6",
        600: "#53389E",
        700: "#42307D",
      },

      // Neutral grayscale
      neutral: {
        0: "#FEFEFE",
        25: "#FCFDFD",
        50: "#F8FAFA",
        75: "#F3F7F8",
        100: "#EEF4F5",
        200: "#E3EAEC",
        300: "#D1DCE0",
        400: "#B8C8CE",
        500: "#9AB0B8",
        600: "#7A959F",
        700: "#5a6c75",
        800: "#3C4F58",
        850: "#2E3F48",
        900: "#23333C",
        950: "#1D2A33",
        1000: "#17262F",
      },

      // Luna AI Assistant brand colors (blue-to-red gradient)
      luna: {
        0: "#5861DE", // Blue (start of gradient) — alias for `start`
        50: "#EEF0FB", // Subtle luna-blue tint
        100: "#ED6F6F", // Red/coral (end of gradient) — alias for `end`
        start: "#5861DE", // Azurite indigo (gradient start)
        end: "#ED6F6F", // Coral (gradient end)
        startHover: "#4349B8", // Indigo on hover
        endHover: "#C24747", // Coral on hover
        dark: {
          0: "#3A3F9C",
          100: "#B83F3F",
        },
        ice: "#FCFDFD", // Ice tone used in luna gradient stops at low alpha
        border: "#D9E0E3", // Light gray-blue used for gradient borders
        // Brand gradient (90deg indigo → coral). Use as `bg="luna.gradient"`.
        gradient: "linear-gradient(90deg, #5861DE 0%, #ED6F6F 100%)",
        gradientHover: "linear-gradient(90deg, #ED6F6F 0%, #5861DE 100%)",
        // Soft tri-stop gradient used on luna cards, banners, callouts.
        // 94deg ice (low alpha) → coral (10%) → indigo (14%).
        gradientSubtle:
          "linear-gradient(94deg, rgba(252,253,253,0.40) 0%, rgba(237,111,111,0.10) 65%, rgba(113,122,247,0.14) 100%)",
        // Even more subtle variant for cards (5/10% opacities).
        gradientCard:
          "linear-gradient(117.219deg, rgba(252,253,253,0.05) 0%, rgba(88,97,222,0.05) 65%, rgba(237,111,111,0.05) 100%)",
        shimmer: {
          light: "#f8f8f8", // Light gray for shimmer effect
          base: "#f0f0f0", // Base gray for shimmer effect
        },
      },

      // Lavender — soft indigo wash used in card--tasks gradient + future
      // azurite-adjacent surfaces.
      lavender: {
        25: "#ECEEFF", // card--tasks gradient end
      },

      // Tasks card gradient (linear, neutral → lavender). Use as `bg="bg.taskCard"` via semantic token or inline.
      bgTasksCard: "linear-gradient(90deg, #FCFDFD 0%, #ECEEFF 100%)",

      // Accent colors
      accents: {
        "electric-blue": "#3B82F6",
        "vibrant-red": "#EF4444",
        "emerald-green": "#10B981",
        "amber-orange": "#F59E0B",
        "royal-purple": "#8B5CF6",
        "cyan-teal": "#06B6D4",
        "hot-pink": "#EC4899",
        "lime-green": "#84CC16",
        "bright-orange": "#F97316",
        "indigo-blue": "#6366F1",
        "teal-mint": "#14B8A6",
      },

      // Decorative/visual effect colors
      decorative: {
        "sky-blue": "#87CEEB", // Pastel blue for morphing blob
        lavender: "#E6E6FA", // Pastel lavender for morphing blob
        "light-pink": "#FFB6C1", // Pastel pink for morphing blob
      },

      // Chart/data visualization colors
      chart: {
        gray: "#999", // Gray text for chart legends
        black: "#000", // Black text for chart hover states
        darkGray: "#555", // Dark gray for backgrounds
        mediumGray: "#888", // Medium gray for backgrounds
        lightGray: "#aaa", // Light gray for backgrounds
      },
    },
    semanticTokens: {
      colors: {
        btn: {
          solid: {
            "primary-bg": "p.600",
            "primary-hover": "p.700",
            "primary-text": "neutral.0",
            "primary-border": "rgba(15, 60, 140, 0.25)",
            "primary-border-hover": "rgba(15, 60, 140, 0.40)",
            "secondary-bg": "neutral.900",
            "secondary-hover": "neutral.800",
            "secondary-text": "neutral.0",
            "secondary-border": "rgba(5, 15, 25, 0.35)",
            "secondary-border-hover": "rgba(5, 15, 25, 0.45)",
            "success-bg": "success.500",
            "success-hover": "success.700",
            "success-text": "neutral.0",
            "success-border": "rgba(4, 80, 60, 0.30)",
            "success-border-hover": "rgba(4, 80, 60, 0.45)",
            "error-bg": "error.500",
            "error-hover": "error.700",
            "error-text": "neutral.0",
            "error-border": "rgba(140, 30, 30, 0.30)",
            "error-border-hover": "rgba(140, 30, 30, 0.45)",
            "warning-bg": "warning.500",
            "warning-hover": "warning.700",
            "warning-text": "neutral.0",
            "warning-border": "rgba(120, 60, 5, 0.30)",
            "warning-border-hover": "rgba(120, 60, 5, 0.45)",
          },
          outline: {
            "primary-text": "p.700",
            "primary-border": "p.600",
            "primary-border-hover": "p.700",
            "primary-hover": "rgba(88, 97, 222, 0.06)",
            "secondary-text": "neutral.1000",
            "secondary-border": "neutral.900",
            "secondary-border-hover": "neutral.1000",
            "secondary-hover": "rgba(35, 51, 60, 0.04)",
            "success-text": "success.700",
            "success-border": "success.200",
            "success-border-hover": "success.500",
            "success-hover": "rgba(16, 185, 129, 0.06)",
            "error-text": "error.700",
            "error-border": "error.200",
            "error-border-hover": "error.500",
            "error-hover": "rgba(255, 82, 79, 0.06)",
            "warning-text": "warning.700",
            "warning-border": "warning.200",
            "warning-border-hover": "warning.500",
            "warning-hover": "rgba(245, 158, 11, 0.06)",
          },
          subtle: {
            "primary-text": "p.600",
            "primary-hover": "p.100",
            "secondary-text": "neutral.1000",
            "secondary-hover": "neutral.100",
            "success-text": "success.700",
            "success-hover": "rgba(16, 185, 129, 0.10)",
            "error-text": "error.700",
            "error-hover": "rgba(255, 82, 79, 0.10)",
            "warning-text": "warning.700",
            "warning-hover": "rgba(245, 158, 11, 0.12)",
            "luna-hover": "p.50",
          },
        },
        // Text semantic tokens (matches Figma Light Mode tokens)
        text: {
          body: {
            primary: "neutral.950",
            secondary: "neutral.850",
            tertiary: "neutral.700",
            invert: "neutral.100",
            success: "success.700",
            warning: "warning.700",
            error: "error.700",
            azurite: "p.600",
          },
          header: {
            default: "neutral.1000",
            invert: "neutral.0",
            tertiary: "p.600",
            success: "success.700",
            warning: "warning.700",
            error: "error.700",
          },
          label: {
            default: "neutral.700",
            secondary: "neutral.600",
            invert: "neutral.300",
            tertiary: "p.500",
            success: "success.600",
            warning: "warning.600",
            error: "error.600",
          },
          paragraph: {
            primary: "neutral.950",
            secondary: "neutral.850",
            tertiary: "neutral.700",
            invert: "neutral.0",
            success: "success.700",
            warning: "warning.700",
            error: "error.700",
          },
          placeholder: {
            default: "neutral.600",
            invert: "neutral.700",
          },
          title: {
            dark: "neutral.1000",
            light: "neutral.0",
            tertiary: "p.600",
          },
          caption: {
            primary: "neutral.950",
            secondary: "neutral.850",
            tertiary: "neutral.700",
            invert: "neutral.0",
            success: "success.700",
            warning: "warning.700",
            error: "error.700",
          },
          disabled: {
            default: "neutral.500",
            invert: "neutral.700",
            tertiary: "p.400",
            success: "success.400",
            warning: "warning.400",
            error: "error.400",
          },
        },
        // Background semantic tokens (matches Figma Light Mode tokens)
        bg: {
          app: "neutral.75",
          page: "neutral.25",
          container: "neutral.25",
          sidebar: "neutral.50",
          elevated: "neutral.50",
          section: "neutral.100",
          input: "neutral.0",
          hover: "neutral.100",
          active: "neutral.200",
          disabled: "neutral.200",
          strong: "neutral.200",
          subtle: "neutral.100",
          component: "neutral.0",
          tooltip: "neutral.950",
          logos: "#000000",
          chips: {
            subtle: "neutral.100",
            strong: "neutral.200",
          },
          overlay: "rgba(0, 0, 0, 0.5)",
          success: "success.50",
          warning: "warning.50",
          error: "error.50",
          azurite: "p.100",
          lunaGradient:
            "linear-gradient(90deg, var(--chakra-colors-luna-0) 0%, var(--chakra-colors-luna-100) 50%, var(--chakra-colors-luna-0) 100%)",
          lunaSolidGradient:
            "linear-gradient(90deg, var(--chakra-colors-luna-0) 0%, var(--chakra-colors-luna-100) 50%, var(--chakra-colors-luna-0) 100%), linear-gradient(90deg, var(--chakra-colors-luna-dark-0) 0%, var(--chakra-colors-luna-dark-100) 50%, var(--chakra-colors-luna-dark-0) 100%)",
          lunaOutlineGradient:
            "linear-gradient(var(--chakra-colors-neutral-0), var(--chakra-colors-neutral-0)), linear-gradient(90deg, var(--chakra-colors-luna-0) 0%, var(--chakra-colors-luna-100) 50%, var(--chakra-colors-luna-0) 100%)",
          lunaGradientSubtle:
            "linear-gradient(133deg, rgba(248, 250, 250, 0.05) 24%, rgba(88, 97, 222, 0.05) 61.64%, rgba(237, 111, 111, 0.05) 74.14%)",
          lunaGradientLinear:
            "linear-gradient(98deg, var(--chakra-colors-luna-0) 5.11%, var(--chakra-colors-luna-100) 101.25%)",
          lunaUsagePanel:
            "linear-gradient(98deg, rgba(252, 253, 253, 0.05) 36.83%, rgba(88, 97, 222, 0.05) 68.56%, rgba(237, 111, 111, 0.05) 101.25%), #FCFDFD",
        },
        // Segmented-control track. Mirrors the main app's `segmented.bg`
        // (ploy-client theme) so the ported SegmentedControl matches it.
        segmented: {
          bg: "neutral.100",
        },
        // Border semantic tokens (matches Figma Light Mode tokens)
        border: {
          default: "neutral.200",
          subtle: "neutral.100",
          strong: "neutral.300",
          hover: "neutral.400",
          // Idle outline of small controls (checkboxes, state markers); the
          // lightest step that clears WCAG 3:1 non-text contrast on white.
          control: "neutral.600",
          focus: "p.500",
          selected: "neutral.900",
          success: "success.500",
          warning: "warning.500",
          error: "error.500",
          disabled: "neutral.200",
        },
      },
    },
  },
);
